import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import './GridView.css';

export default function GridView({ tasks, listId, onSubtaskToggle, onReorderTasks }) {
  const [subtasksData, setSubtasksData] = useState({});
  const [loading, setLoading] = useState(true);
  const [commonSubtasks, setCommonSubtasks] = useState([]);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  // Fetch subtasks for all uncompleted tasks
  useEffect(() => {
    const fetchSubtasks = async () => {
      if (tasks.length === 0) {
        setLoading(false);
        return;
      }

      try {
        const subtasksMap = await api.getSubTasksForList(listId);

        setSubtasksData(subtasksMap);

        // Determine common subtasks from the first task (they should all be the same)
        const firstTaskSubtasks = Object.values(subtasksMap)[0];
        if (firstTaskSubtasks && firstTaskSubtasks.length > 0) {
          // Sort by text (alphabetically) to ensure consistent order regardless of position
          const sorted = [...firstTaskSubtasks].sort((a, b) => a.text.localeCompare(b.text));
          setCommonSubtasks(sorted);
        } else {
          setCommonSubtasks([]);
        }
      } catch (error) {
        console.error('Error fetching subtasks:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubtasks();
  }, [listId, tasks.length]);

  const handleCellClick = async (taskId, subtaskText) => {
    const taskSubtasks = subtasksData[taskId] || [];
    // Find subtask by text only (since IDs differ between tasks and positions may vary)
    const subtask = taskSubtasks.find(st => st.text === subtaskText);
    
    if (!subtask) return;
    
    const newCompleted = !subtask.completed;
    const originalCompleted = subtask.completed;
    
    // Optimistically update UI immediately
    setSubtasksData(prev => ({
      ...prev,
      [taskId]: prev[taskId].map(st =>
        st.id === subtask.id ? { ...st, completed: newCompleted } : st
      )
    }));
    
    // Update in API (fire and forget - optimistic update already done)
    if (onSubtaskToggle) {
      try {
        await onSubtaskToggle(taskId, subtask.id, newCompleted);
      } catch (error) {
        console.error('Error updating sub-task:', error);
        // Revert on error
        setSubtasksData(prev => ({
          ...prev,
          [taskId]: prev[taskId].map(st =>
            st.id === subtask.id ? { ...st, completed: originalCompleted } : st
          )
        }));
      }
    }
  };

  const handleRowDragStart = (e, taskId) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', taskId);
    setDraggingId(taskId);
  };

  const handleRowDragOver = (e, taskId) => {
    e.preventDefault();
    if (draggingId && draggingId !== taskId) {
      setDragOverId(taskId);
    }
  };

  const handleRowDragEnd = () => {
    setDraggingId(null);
    setDragOverId(null);
  };

  const handleRowDrop = (e, targetTaskId) => {
    e.preventDefault();
    const draggedId = e.dataTransfer.getData('text/plain');
    setDraggingId(null);
    setDragOverId(null);

    if (!draggedId || draggedId === targetTaskId || !onReorderTasks) return;

    const ids = tasks.map(t => t.id);
    const fromIndex = ids.indexOf(draggedId);
    const toIndex = ids.indexOf(targetTaskId);
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;

    const reordered = [...ids];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    // TodoList's wrapper appends any tasks not shown here (completed/hidden).
    onReorderTasks(listId, reordered);
  };

  const getSubtaskStatus = (taskId, subtaskText) => {
    const taskSubtasks = subtasksData[taskId] || [];
    const subtask = taskSubtasks.find(st => st.text === subtaskText);
    return subtask?.completed || false;
  };

  if (loading) {
    return (
      <div className="grid-view-loading">
        <div className="spinner"></div>
        <p>Loading grid view...</p>
      </div>
    );
  }

  if (tasks.length === 0 || commonSubtasks.length === 0) {
    return (
      <div className="grid-view-empty">
        <p>No tasks with subtasks to display in grid view.</p>
      </div>
    );
  }

  return (
    <div className="grid-view-container">
      <div className="grid-view-table-wrapper">
        <table className="grid-view-table">
          <thead>
            <tr>
              <th className="grid-view-task-header">Task</th>
              {commonSubtasks.map((subtask) => (
                <th key={subtask.id} className="grid-view-subtask-header">
                  {subtask.text}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => (
              <tr
                key={task.id}
                className={`${draggingId === task.id ? 'grid-view-row-dragging' : ''}${
                  dragOverId === task.id ? ' grid-view-row-drop-target' : ''
                }`}
                onDragOver={(e) => handleRowDragOver(e, task.id)}
                onDrop={(e) => handleRowDrop(e, task.id)}
                onDragEnd={handleRowDragEnd}
              >
                <td
                  className="grid-view-task-cell"
                  draggable
                  onDragStart={(e) => handleRowDragStart(e, task.id)}
                  title="Drag to reorder"
                >
                  {task.text}
                </td>
                {commonSubtasks.map((subtask) => {
                  const isCompleted = getSubtaskStatus(task.id, subtask.text);
                  return (
                    <td
                      key={`${task.id}-${subtask.text}`}
                      className={`grid-view-cell ${isCompleted ? 'grid-view-cell-completed' : ''}`}
                      onClick={() => handleCellClick(task.id, subtask.text)}
                      title={isCompleted ? 'Click to mark incomplete' : 'Click to mark complete'}
                    >
                      {isCompleted && <span className="grid-view-checkmark">✓</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

